// Docs · SDK reference (@firstfund/lender-sdk, firstfund-lender-hub/sdk): overview, defineApi, the applicant, ctx, the
// outcome, createApp, the command-line tools, other exports. Written from sdk/index.js, app.js, discover.js and bin/:
// when the SDK changes, change these pages.
import { C, Code, DocLink, Endpoint, H2, H3, Lead, List, Note, P, Params, Table } from '../parts'

export function SdkOverview() {
  return (
    <>
      <Lead><C>@firstfund/lender-sdk</C> is the one package a lender’s code uses. Plain Node.js 22, no dependencies, already installed in every sandbox and container.</Lead>
      <H2>Install</H2>
      <P>Nothing to install. Inside the Hub’s sandbox and the live containers it is on <C>NODE_PATH</C>, so this just works:</P>
      <Code>{`const { defineApi } = require('@firstfund/lender-sdk')`}</Code>
      <P>Do not add it to <C>package.json</C>. Its code lives in <C>firstfund-lender-hub/sdk</C> (version 0.1.0).</P>
      <H2>What it gives you</H2>
      <Table head={['Export', 'Use it for', 'Page']} rows={[
        ['defineApi(options)', 'every endpoint file: marks and checks the endpoint', <DocLink to="define-api">defineApi</DocLink>],
        ['createApp({ root })', 'src/app.js: serves every endpoint file as HTTP', <DocLink to="create-app">createApp</DocLink>],
        ['ctx (given to handler)', 'call the lender, read variables, the earlier step, logs', <DocLink to="context">ctx</DocLink>],
        ['runEndpoint, makeContext', 'run an endpoint yourself (tests, scripts)', <DocLink to="utilities">Other exports</DocLink>],
        ['checkOutcome, checkCall, blocking', 'the system check on your answer and your call', <DocLink to="utilities">Other exports</DocLink>],
        ['parseDotenv, HubError', 'read a .env text; the SDK’s error type', <DocLink to="utilities">Other exports</DocLink>],
        ['KNOWN_OUTCOMES, HTTP_METHODS, MAX_TIMEOUT_MS, DEFAULT_TIMEOUT_MS', 'the fixed lists and limits', <DocLink to="utilities#constants">Constants</DocLink>],
      ]} />
      <H2>Command-line tools</H2>
      <Table head={['Tool', 'Does', 'Page']} rows={[
        ['hub-run', 'runs one endpoint once (the playground’s Run uses it)', <DocLink to="cli#hub-run">hub-run</DocLink>],
        ['hub-discover', 'writes .hub/manifest.json', <DocLink to="cli#hub-discover">hub-discover</DocLink>],
        ['hub-serve', 'starts a folder as the HTTP service', <DocLink to="cli#hub-serve">hub-serve</DocLink>],
        ['hub-http', 'the playground’s HTTP client, from inside the sandbox', <DocLink to="cli#hub-http">hub-http</DocLink>],
      ]} />
      <H2>The smallest endpoint</H2>
      <Code title="src/api/ping.js  →  POST /ping">{`const { defineApi } = require('@firstfund/lender-sdk')

module.exports = defineApi({
  outcomes: ['ok'],
  async handler(applicant, ctx) {
    return { outcome: 'ok', reply_code: 'PONG' }
  },
})`}</Code>
      <H2>The three promises of the SDK</H2>
      <List>
        <li><b>Same code everywhere</b>: the playground, Build and production run the same file with the same SDK; only <C>ctx.env</C> changes (UAT or production values).</li>
        <li><b>Tests without the lender</b>: in a test event <C>ctx.lender()</C> returns the saved reply.</li>
        <li><b>Checked answers</b>: every call and every answer is checked; anything the system cannot use is refused with a plain reason (<DocLink to="errors">Errors and fixes</DocLink>).</li>
      </List>
    </>
  )
}

export function DefineApi() {
  return (
    <>
      <Lead>Every file under <C>src/api</C> exports one <C>defineApi(…)</C>. It tells the Hub what the endpoint is and gives it the code to run.</Lead>
      <Code>{`module.exports = defineApi({
  title: 'Get offer',
  outcomes: ['offer', 'rejected', 'pending', 'error'],
  method: 'POST',
  async handler(applicant, ctx) {
    // … call the lender, return an outcome
  },
})`}</Code>
      <H2>Options</H2>
      <Params rows={[
        ['handler', 'async function (applicant, ctx)', true, <>The code. Gets the <DocLink to="handler-input">applicant</DocLink> and <DocLink to="context">ctx</DocLink>, returns an <DocLink to="outcome">outcome</DocLink> object. Errors it throws become an <C>error</C> result.</>],
        ['outcomes', 'string[]', 'yes (in practice)', <>Every outcome the handler may return, from <C>ok · offer · approved · redirect · pending · rejected · duplicate · error</C>. <C>error</C> is always added. Each outcome is a line out of the box on the workflow canvas.</>],
        ['title', 'string', false, 'A readable name; shown in the Explorer, the canvas and the API catalogue.'],
        ['method', "'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'", false, <>How the <b>Hub</b> calls this endpoint. Default <C>POST</C>. (The method of the lender call is set in <C>ctx.lender()</C>.)</>],
        ['tokenSource', 'boolean', false, 'Marks the endpoint that gives a login token (used by the Lender Gateway). A workflow never calls it. With the Hub alone, get tokens in code: Recipes → Login token.'],
      ]} />
      <H2>What it returns</H2>
      <P>An object the Hub reads (<C>__hubApi: true</C>, the method, the outcomes with <C>error</C> added, title, tokenSource, handler). You never use it yourself: export it.</P>
      <H2>Checks when the file loads</H2>
      <Table head={['Mistake', 'Message']} rows={[
        ['no object', 'defineApi needs an object: { outcomes, handler }'],
        ['no handler function', 'defineApi needs a handler(input, ctx) function'],
        ["outcomes: ['approve']", 'outcomes approve are not ones the system knows (ok, offer, …)'],
        ["method: 'FETCH'", 'method FETCH is not one of GET, POST, PUT, PATCH, DELETE'],
      ]} />
      <H2>Rules</H2>
      <List>
        <li>One <C>defineApi</C> per file, exported with <C>module.exports =</C> (or <C>export default</C>).</li>
        <li>Write the <C>outcomes</C> list as a plain list in the file: Discover reads it from the text too.</li>
        <li>Return only outcomes that are in the list, or the step is taken as <C>error</C>: “outcome x is not in this endpoint’s outcomes”.</li>
      </List>
    </>
  )
}

export function HandlerInput() {
  return (
    <>
      <Lead>The first argument of <C>handler</C>: one applicant in our standard shape, the same for every lender. Map it to the lender’s field names.</Lead>
      <H2>Fields</H2>
      <Params rows={[
        ['ref', 'string', true, 'Our reference for this application. Send it as the lender’s partner / reference field. Not a person’s id.'],
        ['mobile', 'string', true, '10 digits, e.g. "9800000010"'],
        ['pan', 'string', true, 'e.g. "ABCDE1234F"'],
        ['name', 'string', true, 'full name as typed'],
        ['dob', 'string', true, 'date of birth, YYYY-MM-DD'],
        ['incomeMonth', 'number', true, 'monthly income in rupees'],
        ['employment', "'salaried' | 'self_employed' | 'student' | 'other'", true, 'map to the lender’s own values'],
        ['pincode', 'string', true, '6 digits'],
        ['consentId', 'string', true, 'the consent receipt id from our consent service; most lenders ask for it'],
        ['email', 'string', false, 'may be missing'],
        ['city', 'string', false, 'may be missing'],
        ['loanAmount', 'number', false, 'rupees asked for; may be missing'],
      ]} />
      <H2>The test applicant</H2>
      <P>In the playground and in every test the applicant is made up (never a real person):</P>
      <Code lang="json" title="the test applicant">{`{
  "ref": "rec_demo_1", "mobile": "9800000010", "pan": "ABCDE1234F", "name": "Test Applicant",
  "dob": "1990-01-01", "email": "test.applicant@example.com", "incomeMonth": 25000,
  "employment": "salaried", "pincode": "560001", "city": "Bengaluru",
  "loanAmount": 200000, "consentId": "cmp_demo_consent_1"
}`}</Code>
      <H2>Mapping examples</H2>
      <Code>{`const EMPLOYMENT = { salaried: 'SALARIED', self_employed: 'SELF_EMPLOYED', student: 'OTHERS', other: 'OTHERS' }

const body = {
  partner_ref: applicant.ref,
  full_name: applicant.name,
  mobile_number: applicant.mobile,
  pan_number: applicant.pan,
  date_of_birth: applicant.dob.split('-').reverse().join('/'),   // 1990-01-01 → 01/01/1990
  monthly_income: applicant.incomeMonth,
  employment_type: EMPLOYMENT[applicant.employment],
  pincode: applicant.pincode,
  loan_amount: applicant.loanAmount ?? 100000,                     // a default when missing
}`}</Code>
      <Note kind="warning">This is personal data. Send it only to the lender; never put it in <C>ctx.log</C>, <C>reply_code</C>, <C>message</C> or <C>data</C>.</Note>
    </>
  )
}

export function Context() {
  return (
    <>
      <Lead>The second argument of <C>handler</C>. Everything your code needs from the Hub: the lender call, the variables, the earlier step and a log.</Lead>
      <Table head={['Member', 'Type', 'In short']} rows={[
        ['ctx.lender(call)', 'async → reply', 'call the lender (the only way)'],
        ['ctx.env.NAME', 'string | undefined', 'a variable of tab 5, or LENDER_BASE_URL'],
        ["ctx.secret('NAME')", 'string', 'the same, but stops when missing'],
        ['ctx.prev', 'object', 'what the step before returned'],
        ['ctx.ref', 'string', 'our reference of this application'],
        ['ctx.environment', "'uat' | 'production'", 'where the code runs'],
        ['ctx.mode', "'sample' | 'real'", 'a test event, or a real call'],
        ['ctx.log(...values)', 'function', 'a line in the Console and the run log'],
        ['ctx.fields', '{ key, required, lenderKey, codes }[]', 'this API’s Fields this API needs (tab 3)'],
        ['ctx.code(key, value)', 'function', 'the lender’s code for one of our option values'],
        ['ctx.lenderFields(extra?)', 'function → object', 'every extra detail as the lender names and codes it'],
      ]} />

      <H2 id="ctx-lender">ctx.lender(call)</H2>
      <P>Sends one request to the lender and returns its reply. In a test event (<C>ctx.mode === 'sample'</C>) it sends nothing and returns the saved reply. Every call and reply is recorded for the Console, the run log and Captured codes (keys masked).</P>
      <H3>The call</H3>
      <Params rows={[
        ['method', "'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'", true, 'the lender’s method'],
        ['url', 'string', true, <>an <C>https://</C> address, normally <C>ctx.env.LENDER_BASE_URL + '/path'</C>. Must not contain <C>undefined</C>, <C>null</C> or <C>NaN</C></>],
        ['headers', 'object of strings', false, <>e.g. <C>{"{ Authorization: 'Bearer ' + token }"}</C>. With a body and no Content-Type, <C>application/json</C> is added</>],
        ['body', 'object | string', false, 'an object is sent as JSON; a string is sent as it is (form-urlencoded, XML). Ignored for GET'],
        ['timeoutMs', 'number', false, '1 to 30000. Default 8000 (8 s) with a warning'],
      ]} />
      <H3>The reply</H3>
      <Params rows={[
        ['status', 'number', 'always', <>the HTTP status, or <b>0</b> when there was no reply (timeout, address not found, refused, not whitelisted)</>],
        ['body', 'any', 'always', <>the reply parsed as JSON when it is JSON, else the text; <C>null</C> when empty or no reply</>],
        ['headers', 'object', 'always', 'the reply’s headers, names in lower case'],
      ]} />
      <Code title="reading a reply safely">{`const reply = await ctx.lender({ method: 'GET', url: ctx.env.LENDER_BASE_URL + '/v1/status/' + ctx.prev.lender_ref, headers, timeoutMs: 8000 })
if (reply.status === 0) return { outcome: 'error', reply_code: 'NO_REPLY' }         // timeout or not reachable
const b = reply.body && typeof reply.body === 'object' ? reply.body : {}           // not JSON → {}
if (reply.status >= 500) return { outcome: 'error', reply_code: 'HTTP_' + reply.status }`}</Code>
      <H3>Refused before it goes out</H3>
      <P>A call that breaks a rule is not sent: the handler stops with <C>HubError: the call does not match the system: …</C> and the result is red. The rules: <DocLink to="outcome#the-system-check">the system check</DocLink>.</P>
      <Note>Call the lender only through <C>ctx.lender()</C>. <C>fetch()</C> or a package would work in the playground, but tests could not replace the reply, Captured codes would miss the code and the checks would not run.</Note>

      <H2 id="ctx-env">ctx.env.NAME</H2>
      <P>The lender’s variables for the environment the code runs in: tab 5’s UAT or production value, and <C>LENDER_BASE_URL</C> from tab 1. A name that is not set returns <C>undefined</C> and is listed as a <b>missing variable</b> in the Console and the run log.</P>
      <Code>{`const url = ctx.env.LENDER_BASE_URL + '/v1/leads'
const channel = ctx.env.LENDER_CHANNEL_CODE || 'FIRSTFUND'   // a default when not set`}</Code>
      <P><C>PORT</C>, <C>HUB_ENVIRONMENT</C> and <C>NODE_ENV</C> belong to the runner, not to the lender: never set them in tab 5.</P>

      <H2 id="ctx-secret">ctx.secret('NAME')</H2>
      <P>Like <C>ctx.env.NAME</C>, but throws <C>HubError: ctx.secret('NAME'): no variable NAME for this lender (Env variables tab)</C> when it is not set. Use it for keys: a clear stop is better than a call with <C>undefined</C> in it.</P>
      <Code>{`headers: { 'X-Api-Key': ctx.secret('LENDER_API_KEY') }`}</Code>

      <H2 id="ctx-prev">ctx.prev</H2>
      <P>What the step before in the workflow returned (its outcome object). <C>{'{}'}</C> on the first step. <C>lender_ref</C> is carried along: once a step returned it, later steps still have it even when they did not return it again.</P>
      <Code>{`// step 2 (get offer) after step 1 (create lead) returned { outcome: 'ok', reply_code: 'LEAD_CREATED', lender_ref: 'L-123' }
body: { lead_id: ctx.prev.lender_ref }`}</Code>
      <P>In the playground’s tests <C>ctx.prev</C> is <C>{"{ outcome: 'ok', reply_code: 'LEAD_CREATED', lender_ref: 'DEMO-9001' }"}</C>.</P>

      <H2 id="ctx-ref">ctx.ref</H2>
      <P>Our reference for this application (the same as <C>applicant.ref</C>). Send it to the lender as the partner reference: it ties the lender’s record to ours without personal data.</P>

      <H2 id="ctx-environment">ctx.environment</H2>
      <P><C>'uat'</C> or <C>'production'</C>. Rarely needed: the variables already change with it. Useful for a cache per environment (see the login recipe).</P>

      <H2 id="ctx-mode">ctx.mode</H2>
      <P><C>'sample'</C> in a test event (no real call: every <C>ctx.lender()</C> returns the saved reply), <C>'real'</C> otherwise. Use it to skip a login call in tests.</P>

      <H2 id="ctx-fields">ctx.fields, ctx.code(), ctx.lenderFields()</H2>
      <P>Apply now can ask extra details (employer, home type …) by its field library’s keys. Tab 3 · Lender APIs → <b>Fields</b> says, per API, which of them it needs, the lender’s own name for each and our option value → the lender’s code. The Hub sends that list with every call: <C>ctx.fields</C>. <C>ctx.lenderFields()</C> turns <C>applicant.extra</C> into the lender’s shape (<C>a.b</C> nests); <C>ctx.code(key, value)</C> maps one value. A missing or unmapped value is left out and noted in the log: the application still goes.</P>
      <Code>{`// tab 3: residenceType → address.residenceType, rented = RENTED, owned = OWNED
body: { mobile: applicant.mobile, ...ctx.lenderFields() }      // → { address: { residenceType: 'RENTED' } }
const kind = ctx.code('residenceType', applicant.extra?.residenceType)   // 'RENTED'`}</Code>

      <H2 id="ctx-log">ctx.log(...values)</H2>
      <P>Adds one line to the Console (playground) and to the run’s log. Objects are written as JSON.</P>
      <Code>{`ctx.log('lead reply', reply.status, b.code)          // good: codes and ids
// ctx.log('applicant', applicant)                    // never: personal data`}</Code>
    </>
  )
}

export function Outcome() {
  return (
    <>
      <Lead>What <C>handler</C> returns: one object with the <b>outcome</b> of this step and the lender’s <b>reply_code</b>, plus the fields that outcome needs.</Lead>
      <Code>{`return { outcome: 'offer', reply_code: 'OFFER_2', lender_ref: 'L-123', offers: [{ amount: 200000, tenureMonths: 24, ratePa: 18.5, emi: 10050 }] }`}</Code>
      <H2>Fields</H2>
      <Params rows={[
        ['outcome', 'string', true, <>one of the 8 below, and in the endpoint’s <C>outcomes</C></>],
        ['reply_code', 'string', true, <>the lender’s own code (<C>LEAD_CREATED</C>, <C>E_1043</C>) or <C>HTTP_500</C>, <C>NO_REPLY</C>. Goes to the run log and Captured codes: a code never seen raises an Alert</>],
        ['message', 'string', false, 'text for the user (shown by the app), at most 200 characters'],
        ['lender_ref', 'string', false, 'the lender’s id of the lead / application; later steps read it as ctx.prev.lender_ref'],
        ['offers', 'Offer[]', 'for offer', 'the offers (below)'],
        ['link', 'string (https://)', 'for redirect', 'where our user continues on the lender’s site'],
        ['retryAfterMs', 'number', false, 'for pending: when to ask again'],
        ['token, expiresInS', 'string, number', 'token source', 'only for a tokenSource endpoint'],
        ['products', 'string[]', false, 'product codes the lender named'],
        ['data', 'object', false, 'anything else: kept in the run log only, never sent to the app'],
      ]} />
      <H2>The 8 outcomes</H2>
      <Table head={['outcome', 'Means', 'Needs']} rows={[
        ['ok', 'the step worked, go on to the next', '—'],
        ['offer', 'the lender offers a loan', 'offers with at least one offer'],
        ['approved', 'approved, without a list of offers', '—'],
        ['redirect', 'our user continues on the lender’s page', 'link (https://)'],
        ['pending', 'not decided yet', 'retryAfterMs (optional)'],
        ['rejected', 'the lender said no', 'message (optional)'],
        ['duplicate', 'the lender already has this person', 'message (optional)'],
        ['error', 'anything went wrong', '— (always allowed)'],
      ]} />
      <H2>Offer</H2>
      <Params rows={[
        ['amount', 'number', true, 'rupees, above 0'],
        ['tenureMonths', 'integer', true, 'whole months, above 0'],
        ['ratePa', 'number', true, 'yearly rate in %, above 0 and at most 60 (18.5 means 18.5 %)'],
        ['emi', 'number', false, 'the monthly instalment in rupees, above 0'],
      ]} />
      <H2>The system check</H2>
      <P>Every answer and every lender call is checked. An <b>error</b> blocks (the test is red, the step is <C>error</C>); a <b>warning</b> is only shown.</P>
      <H3>On the answer</H3>
      <Table head={['Rule', 'Level']} rows={[
        ['an object is returned', 'error'],
        ['outcome is one of the 8 and in the endpoint’s outcomes', 'error'],
        ['reply_code is non-empty text', 'error'],
        ['message is text; over 200 characters', 'error · warning'],
        ['lender_ref is text', 'error'],
        ['link starts with https://; redirect has a link', 'error'],
        ['offer has offers; each with amount, tenureMonths, ratePa (and emi) in range', 'error'],
        ['offers given with another outcome', 'warning (ignored)'],
        ['a field not in the list above', 'warning (kept in the run log only: put it under data)'],
      ]} />
      <H3>On the call</H3>
      <Table head={['Rule', 'Level']} rows={[
        ['method is GET, POST, PUT, PATCH or DELETE', 'error'],
        ['url is there, starts with https://, has no undefined / null / NaN', 'error'],
        ['headers are text values', 'error'],
        ['timeoutMs from 1 to 30000; missing → 8000', 'error · warning'],
        ['a body on a GET', 'warning'],
        ['a field other than method, url, headers, body, timeoutMs', 'warning (ignored)'],
      ]} />
      <H2>What the web app finally gets</H2>
      <P>The workflow’s Answer node turns the last step into the standard answer: <C>status</C> (the answer kind), <C>lender_ref</C>, <C>offers</C> (as <C>amount</C>, <C>rate_pa</C>, <C>tenure_months</C>, <C>emi</C>), <C>links.continue</C> (your <C>link</C>), <C>message</C>. See <DocLink to="workflows">Workflows</DocLink>.</P>
    </>
  )
}

export function CreateApp() {
  return (
    <>
      <Lead><C>createApp({'{ root }'})</C> turns the batch folder into an HTTP service. <C>src/app.js</C> is one line around it; the live container runs it.</Lead>
      <Code title="src/app.js">{`const path = require('node:path')
const { createApp } = require('@firstfund/lender-sdk')

module.exports = createApp({ root: path.join(__dirname, '..') })`}</Code>
      <H2>Options</H2>
      <Params rows={[
        ['root', 'string', false, 'the batch folder (default: the current folder)'],
        ['environment', "'uat' | 'production'", false, 'default: HUB_ENVIRONMENT, else uat (set by the runner)'],
      ]} />
      <H2>What it returns</H2>
      <Params rows={[
        ['listen(port?, callback?)', 'function → http.Server', '—', 'starts the service (default PORT or 8080; 0 = any free port) and prints every route'],
        ['handle(req, res)', 'function', '—', 'the request handler, for your own server'],
        ['refresh()', 'function', '—', 'reads the endpoints again'],
        ['manifest', 'object', '—', 'what discovery found'],
        ['root, environment', 'string', '—', 'as given'],
      ]} />
      <H2>The routes it serves</H2>
      <H3>Your endpoints</H3>
      <Endpoint method="POST" path="/<route>" note="the method from defineApi" />
      <Params rows={[
        ['input', 'object', true, 'the applicant'],
        ['prev', 'object', false, 'the earlier step’s outcome (ctx.prev)'],
        ['reply', 'object', false, '{ status, body, headers }: a saved reply; given = test mode'],
        ['mode', "'sample' | 'real'", false, 'default: sample when reply is given, else real'],
      ]} />
      <P>Answers <b>200</b> with the run result (as <DocLink to="cli#hub-run">hub-run</DocLink> prints it), <b>404</b> <C>no_such_endpoint</C> with the known routes, <b>400</b> <C>bad_json</C>, <b>500</b> when the file cannot load.</P>
      <H3>The Hub’s own</H3>
      <Endpoint method="GET" path="/_hub/health" note="{ ok, endpoints, node, environment }" />
      <Endpoint method="GET" path="/_hub/endpoints" note="the manifest, read again" />
      <H2>The variables</H2>
      <P>On every call the service reads <C>.env</C> in the root (written by the Hub from the vault) over the process’s environment, so a changed tab 5 value is used at once.</P>
      <H2>How the Hub calls it in production</H2>
      <P>You do not call containers directly. The Hub’s address runs the live version of a lender:</P>
      <Endpoint method="POST" path="https://hub.firstfundfincorp.com/lenders/{slug}/{route}" note="header x-hub-key; body { input, prev? }" />
    </>
  )
}

const RUN_RESULT = `{
  "ok": true,
  "outcome": "ok",
  "value": { "outcome": "ok", "reply_code": "NEW_CUSTOMER" },
  "error": null,
  "issues": [],
  "calls": [{ "call": { "method": "POST", "url": "https://uat-api.lender.com/lead/dedupe", "headers": { "X-Api-Key": "abcdefg•••" }, "body": { "mobile": "…" } },
              "reply": { "status": 200, "body": { "exists": false }, "headers": {}, "ms": 412 }, "issues": [] }],
  "log": [],
  "envMissing": [],
  "ms": 418
}`

export function Cli() {
  return (
    <>
      <Lead>The SDK’s command-line tools. The playground, Build and the live container use them; you can run them in the playground’s <b>Terminal</b> too.</Lead>
      <H2>hub-run</H2>
      <P>Runs one endpoint once and prints one JSON line. The playground’s Run and the canvas’s Execute use it.</P>
      <Code lang="bash">{`hub-run --file src/api/v1/dedupe.js --input in.json --reply sample.json      # a test (no real call)
hub-run --file src/api/v1/dedupe.js --input in.json --mode real              # really calls the lender (UAT)`}</Code>
      <Params rows={[
        ['--file', 'path', true, 'the endpoint file'],
        ['--input', 'JSON file', false, 'the applicant (default {})'],
        ['--reply', 'JSON file', false, 'a saved reply { status, body, headers }'],
        ['--prev', 'JSON file', false, 'the earlier step’s outcome'],
        ['--mode', 'sample | real', false, 'default sample'],
        ['--environment', 'uat | production', false, 'default uat'],
        ['--root', 'folder', false, 'the batch folder (default: here); its .env is read'],
      ]} />
      <H3>What it prints</H3>
      <Code lang="json" title="the run result">{RUN_RESULT}</Code>
      <Params rows={[
        ['ok', 'boolean', '—', 'no error, an outcome, and no blocking issue'],
        ['outcome', 'string | null', '—', 'the outcome returned'],
        ['value', 'object', '—', 'the whole object returned'],
        ['error', 'string | null', '—', 'what was thrown, e.g. "HubError: …"'],
        ['issues', '{ level, text }[]', '—', 'the system check’s errors and warnings'],
        ['calls', 'array', '—', 'each lender call and its reply (keys masked)'],
        ['log', 'string[]', '—', 'ctx.log and console lines'],
        ['envMissing', 'string[]', '—', 'variables read but not set'],
        ['ms', 'number', '—', 'how long it took'],
      ]} />
      <H2>hub-discover</H2>
      <P>Reads <C>src/api</C> (and an older <C>api</C> folder) and writes <C>.hub/manifest.json</C>. The <b>Discover endpoints</b> button and Build run it. Never write the manifest by hand.</P>
      <Code lang="bash">{`hub-discover              # here
hub-discover --root /work`}</Code>
      <Params rows={[
        ['endpoints', '{ path, method, file, outcomes, title, tokenSource }[]', '—', 'one per endpoint file'],
        ['env', '{ name, files }[]', '—', 'every ctx.env.NAME, process.env.NAME and ctx.secret(\'NAME\') in the code, and where'],
        ['dependencies', 'object', '—', 'from package.json'],
        ['problems', '{ file, text }[]', '—', 'files that do not load, define no endpoint, or repeat a route'],
        ['generatedAt, sdk, node', 'string', '—', 'when, with which SDK and Node'],
      ]} />
      <H2>hub-serve</H2>
      <P>Starts a folder as the HTTP service through <DocLink to="create-app">createApp</DocLink>, for a folder without its own <C>src/server.js</C>. A batch normally runs <C>npm start</C>.</P>
      <Code lang="bash">{`hub-serve --port 8080 --root /work`}</Code>
      <H2>hub-http</H2>
      <P>The playground’s HTTP client sends every request through it, from inside the sandbox (so it leaves from the IP the lender whitelists). It fills in <C>{'{{VARIABLES}}'}</C> from the sandbox’s <C>.env</C>, refuses our private addresses, and masks secrets in the answer. You do not run it yourself.</P>
    </>
  )
}

export function Utilities() {
  return (
    <>
      <Lead>The rest of what the SDK exports. You need them only for your own scripts or tests; endpoint files use <C>defineApi</C> and <C>ctx</C>.</Lead>
      <H2>runEndpoint(api, input, options)</H2>
      <P>Runs one endpoint (the value of <C>defineApi</C>) and returns the <DocLink to="cli#what-it-prints">run result</DocLink>. Options are those of <C>makeContext</C>.</P>
      <Code>{`const { runEndpoint } = require('@firstfund/lender-sdk')
const api = require('./src/api/v1/dedupe.js')

const result = await runEndpoint(api, { ref: 't1', mobile: '9800000010' }, { env: { LENDER_BASE_URL: 'https://x.example', LENDER_API_KEY: 'k' }, mode: 'sample', sample: { status: 200, body: { exists: false } } })
console.log(result.ok, result.outcome)   // true 'ok'`}</Code>
      <H2>makeContext(options)</H2>
      <P>Makes a <C>ctx</C> and the record of what it did (<C>{'{ ctx, record: { calls, log, envMissing } }'}</C>).</P>
      <Params rows={[
        ['env', 'object', false, 'the variables'],
        ['prev', 'object', false, 'ctx.prev'],
        ['ref', 'string', false, 'ctx.ref'],
        ['environment', "'uat' | 'production'", false, 'default uat'],
        ['mode', "'sample' | 'real'", false, 'default sample'],
        ['sample', '{ status, body, headers }', false, 'the reply ctx.lender() returns in sample mode'],
        ['tokenSource', 'boolean', false, 'check the answer as a token source'],
      ]} />
      <H2>checkOutcome(value, options) · checkCall(call) · blocking(issues)</H2>
      <P>The <DocLink to="outcome#the-system-check">system check</DocLink> on its own: each returns a list of <C>{'{ level: \'error\' | \'warning\', text }'}</C>; <C>blocking()</C> keeps the errors.</P>
      <Code>{`const { checkOutcome, blocking } = require('@firstfund/lender-sdk')
blocking(checkOutcome({ outcome: 'offer', reply_code: 'X', offers: [] }))
// [{ level: 'error', text: 'outcome offer needs offers: a list with at least one offer' }]`}</Code>
      <H2>parseDotenv(text)</H2>
      <P>Reads <C>KEY=value</C> lines (comments with <C>#</C>, optional quotes) into an object. No dependency.</P>
      <H2>HubError</H2>
      <P>The SDK’s error: <C>name</C> is <C>'HubError'</C>, <C>issues</C> lists the broken rules. Thrown by <C>defineApi</C>, <C>ctx.secret</C> and a refused <C>ctx.lender()</C>.</P>
      <H2>Constants</H2>
      <Table head={['Name', 'Value']} rows={[
        ['KNOWN_OUTCOMES', "['ok', 'offer', 'approved', 'redirect', 'pending', 'rejected', 'duplicate', 'error']"],
        ['HTTP_METHODS', "['GET', 'POST', 'PUT', 'PATCH', 'DELETE']"],
        ['MAX_TIMEOUT_MS', '30000'],
        ['DEFAULT_TIMEOUT_MS', '8000'],
      ]} />
    </>
  )
}
